import axios from 'axios';

const APP_ID = import.meta.env.VITE_TRAVELTIME_APP_ID;
const API_KEY = import.meta.env.VITE_TRAVELTIME_API_KEY;

if (!APP_ID || !API_KEY) {
    console.error('TravelTime API credentials missing. Please set VITE_TRAVELTIME_APP_ID and VITE_TRAVELTIME_API_KEY in .env');
}

const travelTimeApi = axios.create({
    baseURL: 'https://api.traveltimeapp.com/v4',
    headers: {
        'X-Application-Id': APP_ID || '',
        'X-Api-Key': API_KEY || '',
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Accept-Language': 'en-US', // Required for TravelTime API
    },
});

const isValidCoordinate = (lat: number, lng: number) => {
    return lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
};

export interface TimeMapParams {
    id: string;
    coords: { lat: number; lng: number };
    travelTime: number; // in seconds
    transportation: { type: string };
    departureTime?: string;
    arrivalTime?: string;
}

export interface RouteParams {
    origin: { lat: number; lng: number };
    destination: { lat: number; lng: number };
    transportation: { type: string };
    departureTime?: string;
}

export const getIsochrone = async (params: TimeMapParams) => {
    if (!isValidCoordinate(params.coords.lat, params.coords.lng)) {
        return { results: [] }; // Return empty result instead of throwing
    }

    const searchType = params.departureTime ? 'departure_searches' : 'arrival_searches';
    const payload = {
        [searchType]: [
            {
                id: params.id,
                coords: params.coords,
                [params.departureTime ? 'departure_time' : 'arrival_time']: params.departureTime || params.arrivalTime,
                travel_time: params.travelTime,
                transportation: params.transportation,
            }
        ]
    };

    try {
        const response = await travelTimeApi.post('/time-map', payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching TravelTime Isochrone:', error);
        throw error;
    }
};

export const getRoute = async (params: RouteParams) => {
    if (!isValidCoordinate(params.origin.lat, params.origin.lng) || !isValidCoordinate(params.destination.lat, params.destination.lng)) {
        return { results: [] };
    }

    const payload = {
        locations: [
            { id: 'origin', coords: params.origin },
            { id: 'destination', coords: params.destination },
        ],
        departure_searches: [
            {
                id: 'delivery-route',
                departure_location_id: 'origin',
                arrival_location_ids: ['destination'],
                departure_time: params.departureTime || new Date().toISOString(),
                transportation: params.transportation,
                properties: ['travel_time', 'distance', 'route'],
            }
        ]
    };

    try {
        const response = await travelTimeApi.post('/routes', payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching TravelTime Route:', error);
        throw error;
    }
};

export const geocode = async (query: string) => {
    try {
        const response = await travelTimeApi.get('/geocoding/search', {
            params: { query },
            headers: { 'Content-Type': undefined } // Remove Content-Type for GET requests to avoid 400
        });
        return response.data;
    } catch (error: any) {
        // Log detailed error from API if available
        if (error.response) {
            console.error('TravelTime Geocoding Error:', error.response.status, error.response.data);
        } else {
            console.error('TravelTime Geocoding Error:', error.message);
        }
        throw error;
    }
};

export const reverseGeocode = async (lat: number, lng: number) => {
    try {
        const response = await travelTimeApi.get('/geocoding/reverse', {
            params: { lat, lng },
            headers: { 'Content-Type': undefined }
        });
        return response.data;
    } catch (error: any) {
        if (error.response) {
            console.error('TravelTime Reverse Geocoding Error:', error.response.status, error.response.data);
        } else {
            console.error('TravelTime Reverse Geocoding Error:', error.message);
        }
        throw error;
    }
};

export const decodePolyline = (encoded: string): [number, number][] => {
    let points: [number, number][] = [];
    let index = 0, len = encoded.length;
    let lat = 0, lng = 0;

    while (index < len) {
        let b, shift = 0, result = 0;
        do {
            b = encoded.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lat += dlat;

        shift = 0;
        result = 0;
        do {
            b = encoded.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lng += dlng;

        points.push([lat / 1e5, lng / 1e5]);
    }
    return points;
};


